import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { PolygonalCard } from '../../../../containers/Cards/PolygonalCard/PolygonalCard';
import { FlexContainer } from '../../../../containers/ContentContainers/FlexContainer';
import { SimpleMetric } from '../../../../display/Metrics/SimpleMetric';
import { useTheme } from '@mui/material/styles';
import { UserReecapDto } from '@act/shared/data-transfer-objects';
import { useEffect, useState } from 'react';

interface WorkMetricsDesktopProps {
  guideName: string;
  reecap: UserReecapDto;
}

const GRID_ITEM_MIN_WIDTH = '275px';

export const WorkMetricsDesktop = ({
  guideName,
  reecap,
}: WorkMetricsDesktopProps) => {
  const theme = useTheme();
  const isIpad = useMediaQuery('(pointer: coarse)');
  const [showVisits, setShowVisits] = useState(false);
  const [showCallsMade, setShowCallsMade] = useState(false);
  const [showTextsSent, setShowTextsSent] = useState(false);

  const mainGridGap = isIpad ? 8 : 24;
  const flexGap = isIpad ? 1 : 2;
  const stackGap = isIpad ? 3 : 7;
  const cardHeight = isIpad ? '160px' : '200px';
  const headerFontSize = isIpad ? '44px' : '48px';
  const contentMargin = isIpad ? '0 0 20px' : '40px 0';

  const {
    callsThisYear = 0,
    membersServedThisYear = 0,
    needsAddressedThisYear = 0,
    needsIdentifiedThisYear = 0,
    pcpConnectionsThisYear = 0,
    textsThisYear = 0,
    visitsThisYear = 0,
  } = reecap;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVisits(true);
      setShowCallsMade(true);
      setShowTextsSent(true);
    }, 7350);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -25 }}
        animate={{
          opacity: [0, 1, 1],
          scale: isIpad ? [1, 1, 0.5] : [1, 1, 0.6667],
          y: isIpad ? [-25, 0, 10] : [-25, 0, 35],
        }}
        transition={{
          duration: 4.5,
          times: [0, 0.33, 1],
          ease: 'easeInOut',
        }}
        style={{
          transformOrigin: 'center top',
        }}
      >
        <FlexContainer
          direction="column"
          height="fit-content"
          alignItems="center"
          gap={flexGap}
        >
          <Typography
            variant="headlineLarge"
            fontSize={headerFontSize}
            fontWeight="500"
            color="primary.contrastText"
          >
            This year you did
          </Typography>
          <Typography
            variant="headlineLarge"
            fontSize={headerFontSize}
            fontWeight="500"
            color="primary.contrastText"
          >
            phenomenal work,
          </Typography>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: [0, 1, 1],
              y: [10, 0, 0],
            }}
            transition={{
              duration: 4.5,
              times: [0, 0.33, 1],
              delay: 1.5,
              ease: 'easeInOut',
            }}
          >
            <Typography
              variant="headlineLarge"
              fontSize={headerFontSize}
              fontWeight="500"
              color={theme.palette.primaryAccent.main}
            >
              {guideName}
            </Typography>
          </motion.div>
        </FlexContainer>
      </motion.div>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          margin: contentMargin,
        }}
      >
        <Grid
          container
          gap={mainGridGap}
          sx={{
            maxWidth: 'fit-content',
            placeItems: 'center',
          }}
        >
          <Grid item xs="auto" sx={{ minWidth: GRID_ITEM_MIN_WIDTH }}>
            <Stack direction="column" gap={stackGap}>
              <FlexContainer
                direction="column"
                height="fit-content"
                alignItems="center"
                gap={2}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.5, duration: 0.8 }}
                >
                  <PolygonalCard polygonName="hexagon" height={cardHeight}>
                    <SimpleMetric
                      iconName="User"
                      metricValue={membersServedThisYear}
                      metricSize="xl"
                    />
                  </PolygonalCard>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.5, duration: 0.8 }}
                >
                  <Typography
                    variant="headlineLarge"
                    fontWeight="500"
                    color="primary.contrastText"
                  >
                    Members impacted
                  </Typography>
                </motion.div>
              </FlexContainer>
              <FlexContainer
                direction="column"
                height="fit-content"
                alignItems="center"
                gap={2}
              >
                <AnimatePresence mode="wait">
                  {!showVisits ? (
                    <motion.div
                      key="needs-identified-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ delay: 5.25, duration: 0.8 }}
                    >
                      <PolygonalCard polygonName="heptagon" height={cardHeight}>
                        <SimpleMetric
                          iconName="HandHeart"
                          metricValue={needsIdentifiedThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="visits-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <PolygonalCard polygonName="heptagon" height={cardHeight}>
                        <SimpleMetric
                          iconName="HouseLine"
                          metricValue={visitsThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  {!showVisits ? (
                    <motion.div
                      key="needs-identified-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 5.25, duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        Needs identified
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="home-visits-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        Home visits
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FlexContainer>
            </Stack>
          </Grid>
          <Grid item xs="auto" sx={{ minWidth: GRID_ITEM_MIN_WIDTH }}>
            <Stack direction="column" gap={stackGap}>
              <FlexContainer
                direction="column"
                height="fit-content"
                alignItems="center"
                gap={flexGap}
              >
                <AnimatePresence mode="wait">
                  {!showTextsSent ? (
                    <motion.div
                      key="pcp-connections-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ delay: 3.75, duration: 0.8 }}
                    >
                      <PolygonalCard
                        polygonName="tetrahedron_diamond"
                        height={cardHeight}
                      >
                        <SimpleMetric
                          iconName="Stethoscope"
                          metricValue={pcpConnectionsThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="texts-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <PolygonalCard
                        polygonName="tetrahedron_diamond"
                        height={cardHeight}
                      >
                        <SimpleMetric
                          iconName="ChatText"
                          metricValue={textsThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  {!showTextsSent ? (
                    <motion.div
                      key="pcp-connections-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 3.75, duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        PCP connections
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="texts-sent-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        Texts sent
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FlexContainer>
              <FlexContainer
                direction="column"
                height="fit-content"
                alignItems="center"
                gap={2}
              >
                <AnimatePresence mode="wait">
                  {!showCallsMade ? (
                    <motion.div
                      key="needs-addressed-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ delay: 6.75, duration: 0.8 }}
                    >
                      <PolygonalCard polygonName="pentagon" height={cardHeight}>
                        <SimpleMetric
                          iconName="CheckCircle"
                          metricValue={needsAddressedThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="calls-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <PolygonalCard polygonName="pentagon" height={cardHeight}>
                        <SimpleMetric
                          iconName="PhoneCall"
                          metricValue={callsThisYear}
                          metricSize="xl"
                        />
                      </PolygonalCard>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AnimatePresence mode="wait">
                  {!showCallsMade ? (
                    <motion.div
                      key="needs-addressed-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 6.75, duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        Needs addressed
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="calls-made-label"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Typography
                        variant="headlineLarge"
                        fontWeight="500"
                        color="primary.contrastText"
                      >
                        Calls made
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </FlexContainer>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
